<div class="min-h-full bg-transparent px-4 xl:px-6 py-6 space-y-5" id="agent_tool_page">

  <!-- Toolbar -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <p class="text-sm text-slate-500 dark:text-slate-400">
      Function tools the live-call assistant can call for this product, and which assistants use them.
    </p>
    <div class="flex items-center gap-2 flex-wrap">
      <label class="flex items-center gap-2 cursor-pointer select-none">
        <button
          type="button"
          (click)="toggleIncludeInactive()"
          class="relative w-9 h-5 rounded-full transition-colors duration-200"
          [class.bg-indigo-500]="includeInactive()"
          [class.bg-slate-200]="!includeInactive()"
          [class.dark:bg-slate-700]="!includeInactive()"
          id="agent_tool_include_inactive_toggle"
        >
          <span
            class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200"
            [class.translate-x-4]="includeInactive()"
            [class.translate-x-0]="!includeInactive()"
          ></span>
        </button>
        <span class="text-xs font-medium text-slate-500 dark:text-slate-400">Show inactive</span>
      </label>

      <button
        (click)="loadTools()"
        [disabled]="loading()"
        class="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700
               text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50
               transition-colors disabled:opacity-50"
        id="agent_tool_refresh_btn"
      >
        <svg class="w-3.5 h-3.5" [class.animate-spin]="loading()"
             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 10"></polyline>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
        </svg>
        Refresh
      </button>

      <button
        (click)="openPanel('create', null)"
        class="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white
               bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700
               shadow-sm shadow-purple-500/25 transition-all active:scale-95"
        id="agent_tool_add_btn"
      >
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        New Function Tool
      </button>
    </div>
  </div>

  <!-- Error state -->
  <div
    *ngIf="errorMsg()"
    class="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-sm text-red-700 dark:text-red-400"
    id="agent_tool_error"
  >
    {{ errorMsg() }}
  </div>

  <!-- Loading skeleton -->
  <div *ngIf="loading() && !tools().length" class="space-y-2">
    <div *ngFor="let i of [1,2,3,4]" class="h-14 rounded-xl bg-slate-100 dark:bg-slate-800/50 animate-pulse"></div>
  </div>

  <!-- Table -->
  <div
    *ngIf="!loading() || tools().length"
    class="bg-white dark:bg-surface-2/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 overflow-hidden shadow-sm"
  >
    <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-700/50">
      <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Tools</p>
    </div>

    <p *ngIf="!tools().length" class="py-16 text-center text-slate-400 dark:text-slate-500 text-sm">
      No function tools yet — create one with "New Function Tool".
    </p>

    <div *ngIf="tools().length" class="overflow-x-auto">
      <table class="w-full text-sm" id="agent_tool_table">
        <thead>
          <tr class="border-b border-slate-100 dark:border-slate-700/50">
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Name</th>
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden lg:table-cell">Method</th>
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden lg:table-cell">Endpoint</th>
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden xl:table-cell">Auth</th>
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden xl:table-cell">Agents</th>
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Environment</th>
            <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden xl:table-cell">Updated</th>
            <th class="px-4 py-3 text-center text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Active</th>
            <th class="px-4 py-3 text-right text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let tool of paginatedTools(); trackBy: trackByTool"
            class="border-b border-slate-50 dark:border-slate-700/30 hover:bg-slate-50/80 dark:hover:bg-slate-700/20 transition-colors"
            [class.opacity-50]="tool.isActive === false"
            [attr.id]="'agent_tool_row_' + tool.id"
          >
            <td class="px-4 py-3.5">
              <p class="font-semibold text-slate-800 dark:text-white text-sm leading-tight">{{ tool.toolName }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5 font-mono">{{ tool.toolKeyname }}</p>
            </td>

            <td class="px-4 py-3.5 hidden lg:table-cell">
              <code class="text-[10px] bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-md font-mono">
                {{ tool.method }}
              </code>
            </td>

            <td class="px-4 py-3.5 hidden lg:table-cell max-w-[220px]">
              <p class="text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate" [title]="tool.baseUrl">
                {{ tool.baseUrl }}
              </p>
            </td>

            <td class="px-4 py-3.5 hidden xl:table-cell">
              <span class="text-xs text-slate-500 dark:text-slate-400">{{ tool.authName ?? '—' }}</span>
            </td>

            <td class="px-4 py-3 hidden xl:table-cell">
              <div class="flex flex-wrap gap-1 max-w-[220px]">
                <span *ngFor="let agent of tool.agents"
                      class="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/30
                             text-purple-600 dark:text-purple-400 font-semibold truncate max-w-[100px]"
                      [title]="agent.name">
                  {{ agent.name }}
                </span>
                <span *ngIf="!tool.agents.length" class="text-[10px] text-slate-400">no agents</span>
              </div>
            </td>

            <td class="px-4 py-3.5">
              <select
                class="text-[10px] font-semibold rounded-md px-2 py-1 border-0 cursor-pointer
                       focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                [ngClass]="envBadgeClass(tool.forEnv)"
                [ngModel]="tool.forEnv"
                (ngModelChange)="requestEnvChange(tool, $event)"
                title="Where this tool is visible — pick a new environment to move it"
                [attr.id]="'agent_tool_env_select_' + tool.id"
              >
                <option *ngFor="let opt of envOptions" [ngValue]="opt.value">{{ opt.label }}</option>
              </select>
            </td>

            <td class="px-4 py-3.5 hidden xl:table-cell">
              <span class="text-xs text-slate-500 dark:text-slate-400">{{ tool.updatedAt || '—' }}</span>
            </td>

            <td class="px-4 py-3.5 text-center">
              <button
                type="button"
                (click)="toggleActive(tool)"
                class="relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none"
                [class.bg-emerald-500]="tool.isActive"
                [class.bg-slate-200]="!tool.isActive"
                [class.dark:bg-slate-700]="!tool.isActive"
                [title]="tool.isActive ? 'Click to deactivate' : 'Click to activate'"
                [attr.id]="'agent_tool_active_toggle_' + tool.id"
              >
                <span
                  class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200"
                  [class.translate-x-4]="tool.isActive"
                  [class.translate-x-0]="!tool.isActive"
                ></span>
              </button>
            </td>

            <td class="px-4 py-3.5">
              <div class="flex items-center justify-end gap-1">
                <button (click)="openPanel('edit', tool)"
                        class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
                        title="Edit tool" [attr.id]="'agent_tool_edit_btn_' + tool.id">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>

                <button (click)="confirmDelete(tool)"
                        class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                        title="Delete tool" [attr.id]="'agent_tool_delete_btn_' + tool.id">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                    <path d="M10 11v6M14 11v6"></path>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="p-3 border-t border-slate-100 dark:border-slate-700/50" *ngIf="tools().length > 0">
      <app-pagination
        [currentPage]="pager.page()"
        [totalItems]="tools().length"
        [pageSize]="pager.pageSize()"
        [pageSizes]="[5, 10, 15, 20]"
        (pageChange)="pager.setPage($event)"
        (pageSizeChange)="pager.setPageSize($event)"
      ></app-pagination>
    </div>
  </div>
</div>

<!-- Delete confirmation -->
<div
  *ngIf="deleteTarget()"
  class="fixed inset-0 z-[9000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  id="agent_tool_delete_modal"
>
  <div class="bg-white dark:bg-surface-1 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" (click)="$event.stopPropagation()">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      </div>
      <h3 class="text-sm font-bold text-slate-800 dark:text-white">Delete Tool?</h3>
    </div>

    <p class="text-xs text-slate-600 dark:text-slate-300 mb-5">
      Delete <strong>{{ deleteTarget()!.toolName }}</strong>?
    </p>

    <div class="flex gap-3">
      <button (click)="cancelDelete()"
              class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              id="agent_tool_delete_cancel_btn">Cancel</button>
      <button (click)="executeDelete()" [disabled]="deleting()"
              class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 transition-colors"
              id="agent_tool_delete_confirm_btn">
        <span *ngIf="!deleting()">Delete</span>
        <span *ngIf="deleting()" class="flex items-center justify-center gap-2">
          <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          Deleting…
        </span>
      </button>
    </div>
  </div>
</div>

<!-- Environment-move confirmation -->
<div
  *ngIf="envChangeTarget()"
  class="fixed inset-0 z-[9000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  id="agent_tool_env_modal"
>
  <div class="bg-white dark:bg-surface-1 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" (click)="$event.stopPropagation()">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <svg class="w-5 h-5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 1l4 4-4 4"></path>
          <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
          <path d="M7 23l-4-4 4-4"></path>
          <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
        </svg>
      </div>
      <h3 class="text-sm font-bold text-slate-800 dark:text-white">Move to {{ envLabel(envChangeTarget()!.to) }}?</h3>
    </div>

    <p class="text-xs text-slate-600 dark:text-slate-300 mb-5">
      <strong>{{ envChangeTarget()!.tool.toolName }}</strong> will be visible in
      <strong>{{ envLabel(envChangeTarget()!.to) }}</strong>.
      <span *ngIf="envIncludesProduction(envChangeTarget()!.to)">
        Production live calls can start using it immediately.
      </span>
      <span *ngIf="!envIncludesProduction(envChangeTarget()!.to)">
        It will no longer be visible to production.
      </span>
    </p>

    <div class="flex gap-3">
      <button (click)="cancelEnvChange()"
              class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              id="agent_tool_env_cancel_btn">Cancel</button>
      <button (click)="executeEnvChange()" [disabled]="changingEnv()"
              class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 transition-colors"
              id="agent_tool_env_confirm_btn">
        <span *ngIf="!changingEnv()">Move</span>
        <span *ngIf="changingEnv()" class="flex items-center justify-center gap-2">
          <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          Moving…
        </span>
      </button>
    </div>
  </div>
</div>

<!-- Create / edit panel -->
<app-tool-form-panel
  *ngIf="panelOpen()"
  [mode]="panelMode()"
  [tool]="selectedTool()"
  (saved)="onSaved()"
  (closed)="closePanel()"
></app-tool-form-panel>
